import { getNodeServiceBaseUrl } from '../config/apiBase';

const AR_BASE_URL = getNodeServiceBaseUrl();

const getAuthToken = () => {
  // Use the same token key as the rest of the application
  return localStorage.getItem('zTaskAuthToken') || '';
};

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getAuthToken()}`,
});

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || 'Request failed');
  }
  return response.json();
};

const arService = {
  // Debit Notes
  getDebitNotes: async () => {
    const response = await fetch(`${AR_BASE_URL}/debit-note/api/debit-notes`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getDebitNoteById: async (id) => {
    const response = await fetch(`${AR_BASE_URL}/debit-note/api/debit-notes/${id}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  createDebitNote: async (data) => {
    const response = await fetch(`${AR_BASE_URL}/debit-note/api/debit-notes`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  updateDebitNote: async (id, data) => {
    const response = await fetch(`${AR_BASE_URL}/debit-note/api/debit-notes/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  deleteDebitNote: async (id, deletion_reason) => {
    const response = await fetch(`${AR_BASE_URL}/debit-note/api/debit-notes/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
      body: JSON.stringify({ deletion_reason }),
    });
    return handleResponse(response);
  },

  sendDebitNoteForApproval: async (id) => {
    const response = await fetch(`${AR_BASE_URL}/debit-note/api/debit-notes/${id}/send-for-approval`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({}),
    });
    return handleResponse(response);
  },

  approveDebitNote: async (id) => {
    const response = await fetch(`${AR_BASE_URL}/debit-note/api/debit-notes/${id}/approve`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({}),
    });
    return handleResponse(response);
  },

  rejectDebitNote: async (id, approval_remarks) => {
    const response = await fetch(`${AR_BASE_URL}/debit-note/api/debit-notes/${id}/reject`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ approval_remarks }),
    });
    return handleResponse(response);
  },

  // Credit Notes
  getCreditNotes: async () => {
    const response = await fetch(`${AR_BASE_URL}/credit-note/api/credit-notes`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getCreditNoteById: async (id) => {
    const response = await fetch(`${AR_BASE_URL}/credit-note/api/credit-notes/${id}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  createCreditNote: async (data) => {
    const response = await fetch(`${AR_BASE_URL}/credit-note/api/credit-notes`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  updateCreditNote: async (id, data) => {
    const response = await fetch(`${AR_BASE_URL}/credit-note/api/credit-notes/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  deleteCreditNote: async (id, deletion_reason) => {
    const response = await fetch(`${AR_BASE_URL}/credit-note/api/credit-notes/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
      body: JSON.stringify({ deletion_reason }),
    });
    return handleResponse(response);
  },

  sendCreditNoteForApproval: async (id) => {
    const response = await fetch(`${AR_BASE_URL}/credit-note/api/credit-notes/${id}/send-for-approval`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({}),
    });
    return handleResponse(response);
  },

  approveCreditNote: async (id) => {
    const response = await fetch(`${AR_BASE_URL}/credit-note/api/credit-notes/${id}/approve`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({}),
    });
    return handleResponse(response);
  },

  rejectCreditNote: async (id, approval_remarks) => {
    const response = await fetch(`${AR_BASE_URL}/credit-note/api/credit-notes/${id}/reject`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ approval_remarks }),
    });
    return handleResponse(response);
  },

  // Receipts
  getReceipts: async () => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getReceiptById: async (id) => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts/${id}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  createReceipt: async (data) => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  updateReceipt: async (id, data) => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  deleteReceipt: async (id, deletion_reason) => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
      body: JSON.stringify({ deletion_reason }),
    });
    return handleResponse(response);
  },

  getPendingInvoices: async (customerId) => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts/pending-invoices/${customerId}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getCustomerOutstandingInvoices: async (customerId) => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts/pending-invoices/${customerId}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  approveReceipt: async (id, approvalRemark = '') => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts/${id}/approve`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ approval_remarks: approvalRemark }),
    });
    return handleResponse(response);
  },

  rejectReceipt: async (id, approvalRemark) => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts/${id}/reject`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ approval_remarks: approvalRemark }),
    });
    return handleResponse(response);
  },

  // Customer Sites
  getCustomerSites: async () => {
    const response = await fetch(`${AR_BASE_URL}/customer-site/api/customer-sites`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getCustomerSiteById: async (id) => {
    const response = await fetch(`${AR_BASE_URL}/customer-site/api/customer-sites/${id}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getCustomerSitesByCustomer: async (customerId) => {
    const response = await fetch(`${AR_BASE_URL}/customer-site/api/customer-sites/customer/${customerId}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  createCustomerSite: async (data) => {
    const response = await fetch(`${AR_BASE_URL}/customer-site/api/customer-sites`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  updateCustomerSite: async (id, data) => {
    const response = await fetch(`${AR_BASE_URL}/customer-site/api/customer-sites/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  deleteCustomerSite: async (id) => {
    const response = await fetch(`${AR_BASE_URL}/customer-site/api/customer-sites/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Invoices (for reference in AR modules)
  getInvoicesByCustomer: async (customerId) => {
    const response = await fetch(`${AR_BASE_URL}/invoice/api/invoices?cust_id=${customerId}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Payment Terms
  getPaymentTerms: async () => {
    const response = await fetch(`${AR_BASE_URL}/payment-term/api/payment-terms`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getPaymentTermById: async (id) => {
    const response = await fetch(`${AR_BASE_URL}/payment-term/api/payment-terms/${id}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  createPaymentTerm: async (data) => {
    const response = await fetch(`${AR_BASE_URL}/payment-term/api/payment-terms`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  updatePaymentTerm: async (id, data) => {
    const response = await fetch(`${AR_BASE_URL}/payment-term/api/payment-terms/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  deletePaymentTerm: async (id) => {
    const response = await fetch(`${AR_BASE_URL}/payment-term/api/payment-terms/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Opening Balance
  getOpeningBalance: async (customerId) => {
    const response = await fetch(`${AR_BASE_URL}/opening-balance/api/opening-balance/${customerId}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  updateOpeningBalance: async (customerId, data) => {
    const response = await fetch(`${AR_BASE_URL}/opening-balance/api/opening-balance/${customerId}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // Customers (for dropdowns)
  getCustomers: async () => {
    const response = await fetch(`${AR_BASE_URL}/customer/api/customers`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getCustomersWithOutstanding: async () => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts/customers-with-outstanding`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Finance Helper APIs
  getProjectsByCustomer: async (customerId) => {
    const response = await fetch(`${AR_BASE_URL}/finance-helpers/api/customers/${customerId}/projects`, {
      method: 'GET',
      headers: getHeaders(),
    });
    const data = await handleResponse(response);
    return data.projects || [];
  },

  getInvoicesByProject: async (projectId) => {
    const response = await fetch(`${AR_BASE_URL}/finance-helpers/api/projects/${projectId}/invoices`, {
      method: 'GET',
      headers: getHeaders(),
    });
    const data = await handleResponse(response);
    return data.invoices || [];
  },

  getInvoicesByCustomerWithDetails: async (customerId) => {
    const response = await fetch(`${AR_BASE_URL}/finance-helpers/api/customers/${customerId}/invoices`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getWorkOrderDetailsByInvoice: async (invoiceId) => {
    const response = await fetch(`${AR_BASE_URL}/finance-helpers/api/invoices/${invoiceId}/work-order-details`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getProjectDetails: async (projectId) => {
    const response = await fetch(`${AR_BASE_URL}/finance-helpers/api/projects/${projectId}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getUsersByApprovalRole: async (roleCode) => {
    const response = await fetch(`${AR_BASE_URL}/finance-helpers/api/users-by-approval-role/${encodeURIComponent(roleCode)}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Permission-based approver APIs (NEW)
  getDebitNoteApprovers: async () => {
    const response = await fetch(`${AR_BASE_URL}/approval-roles/api/approvers/debit-note`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getCreditNoteApprovers: async () => {
    const response = await fetch(`${AR_BASE_URL}/approval-roles/api/approvers/credit-note`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getReceiptApprovers: async () => {
    const response = await fetch(`${AR_BASE_URL}/approval-roles/api/approvers/receipt`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Common Master APIs
  getCommonMasterData: async (tableName, columnName, filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await fetch(`${AR_BASE_URL}/api/get-column/${tableName}/${columnName}${queryParams ? '?' + queryParams : ''}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getBanks: async () => {
    return await arService.getCommonMasterData('common_master', 'bank_name', { active: 1 });
  },

  // Deduction Types API
  getDeductionTypes: async () => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts/deduction-types`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Customer Balance API
  getCustomerBalance: async (customerId) => {
    const response = await fetch(`${AR_BASE_URL}/receipt/api/receipts/customer-balance/${customerId}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getCustomerInvoicePayments: async (customerId) => {
    const response = await fetch(`${AR_BASE_URL}/finance-helpers/api/customers/${customerId}/invoice-payments`, {
      method: 'GET',
      headers: getHeaders(),
    });
    const data = await handleResponse(response);
    return data.invoices || [];
  },

  // Customer Ledger APIs
  getCustomerLedger: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    
    const response = await fetch(`${AR_BASE_URL}/customer-ledger/api/ledger?${params}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getCustomerSummary: async (customerId) => {
    const response = await fetch(`${AR_BASE_URL}/customer-ledger/api/customer-summary/${customerId}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getOutstandingReport: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    
    const response = await fetch(`${AR_BASE_URL}/customer-ledger/api/outstanding-report?${params}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  getAgingReport: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    
    const response = await fetch(`${AR_BASE_URL}/customer-ledger/api/aging-report?${params}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Export APIs
  exportLedger: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    params.append('format', 'excel');
    
    const response = await fetch(`${AR_BASE_URL}/customer-ledger/api/export-ledger?${params}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  exportOutstanding: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    params.append('format', 'excel');
    
    const response = await fetch(`${AR_BASE_URL}/customer-ledger/api/export-outstanding?${params}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  exportAging: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    params.append('format', 'excel');
    
    const response = await fetch(`${AR_BASE_URL}/customer-ledger/api/export-aging?${params}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  exportReceiptRegister: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    params.append('format', 'excel');
    
    const response = await fetch(`${AR_BASE_URL}/reports/api/receipt-register?${params}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  exportAllocationReport: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    params.append('format', 'excel');
    
    const response = await fetch(`${AR_BASE_URL}/reports/api/receipt-allocation?${params}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  exportTDSReport: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    params.append('format', 'excel');
    
    const response = await fetch(`${AR_BASE_URL}/reports/api/tds-report?${params}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },
};

export default arService;
